import React from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';

import { useTranslation } from '../../../common/hooks/useTranslation';
import {
  KSFStatusEnum,
  SalesInvoiceStatusEnum,
} from '../../../common/pokCore/validation/enums';
import { GetExtendedSalesInvoiceDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import momencik from '../../../common/momencik';

interface KsefMessage {
  text: string;
  isError?: boolean;
}

interface CorrectionsInfoProps {
  salesInvoice: GetExtendedSalesInvoiceDto;
  goToSalesInvoice: (id: string) => void;
}

const CorrectionsInfo: React.FC<CorrectionsInfoProps> = ({
  salesInvoice,
  goToSalesInvoice,
}) => {
  const { t, tk } = useTranslation('fvsLng');

  return (
    <li>
      {salesInvoice.originalInvoice?.id
        ? t(tk.invoice.withCorrections)
        : t(tk.invoice.withCorrectionsToOriginal)}
      :{' '}
      {salesInvoice.correctionsNumbers?.map((num, idx) => {
        const id = salesInvoice.correctionsIds?.[idx];
        return (
          <React.Fragment key={idx}>
            {idx > 0 && ', '}
            {id && (
              <Button
                variant="link"
                className="p-0 align-baseline small"
                onClick={() => goToSalesInvoice(id)}
              >
                {num}
              </Button>
            )}
          </React.Fragment>
        );
      })}
    </li>
  );
};

interface EmailInfoProps {
  salesInvoice: GetExtendedSalesInvoiceDto;
}

const EmailInfo: React.FC<EmailInfoProps> = ({ salesInvoice }) => {
  const { t, tk } = useTranslation('fvsLng');

  return (
    <li>
      <OverlayTrigger
        overlay={
          <Tooltip>
            {t(tk.finance.sentByEmail)}{' '}
            {salesInvoice.emailSendDate &&
              momencik(salesInvoice.emailSendDate, 'YYYY-MM-DD HH:mm:ss')}
          </Tooltip>
        }
      >
        <span className="sales-invoice-header-email-label">
          {t(tk.finance.sentEmail)}
        </span>
      </OverlayTrigger>{' '}
      <span className="small">
        {salesInvoice.emailsTo}
        {salesInvoice.emailsDw && `, ${salesInvoice.emailsDw}`}
      </span>
    </li>
  );
};

interface SalesInvoiceHeaderInfoProps {
  salesInvoice: GetExtendedSalesInvoiceDto;
  goToSalesInvoice: (id: string) => void;
}

export const SalesInvoiceHeaderInfo: React.FC<SalesInvoiceHeaderInfoProps> = ({
  salesInvoice,
  goToSalesInvoice,
}) => {
  const { t, tk } = useTranslation('fvsLng');

  const getKsefMessages = (): KsefMessage[] => {
    const { status, statusKsef } = salesInvoice;
    const isSent = status === SalesInvoiceStatusEnum.Sent;

    if (statusKsef === KSFStatusEnum.RejectedInKSeF) {
      return [
        {
          text: t(tk.finance.rejectedFromKSeFNotToSH),
          isError: true,
        },
        {
          text: `${t(tk.finance.rejectionReason)}: ${
            salesInvoice.ksefRejectionReason || t(tk.finance.noRejectionReason)
          }`,
          isError: true,
        },
      ];
    }
    if (salesInvoice.toKsef && status === SalesInvoiceStatusEnum.Generated) {
      return [{ text: t(tk.finance.subjectToKSeF) }];
    }
    if (isSent && statusKsef === KSFStatusEnum.WaitingForKSeF) {
      return [{ text: t(tk.finance.waitingForKSeFNumber) }];
    }
    if (isSent && statusKsef === KSFStatusEnum.OFFLINE) {
      return [{ text: t(tk.finance.offline) }];
    }
    if (status === SalesInvoiceStatusEnum.InKSeF && salesInvoice.numberKsef) {
      const acceptanceDate = salesInvoice.ksefDate
        ? ` · ${t(tk.finance.ksefAcceptanceDate)}: ${momencik(salesInvoice.ksefDate)}`
        : '';
      return [
        {
          text: `${t(tk.finance.ksefNumber)}: ${salesInvoice.numberKsef}${acceptanceDate}`,
        },
      ];
    }
    return [];
  };

  const ksefMessages = getKsefMessages();
  const isKsefError = ksefMessages.some(({ isError }) => isError);

  return (
    <>
      {!!ksefMessages.length && (
        <li className={`fw-bold ${isKsefError ? 'text-danger' : 'text-green'}`}>
          {ksefMessages.map(({ text }) => text).join(' · ')}
        </li>
      )}
      {!!salesInvoice.correctionsNumbers?.length && (
        <CorrectionsInfo
          salesInvoice={salesInvoice}
          goToSalesInvoice={goToSalesInvoice}
        />
      )}
      {salesInvoice.emailSent && <EmailInfo salesInvoice={salesInvoice} />}
    </>
  );
};
